// What a thought in the Sky of Thoughts is mostly about. Shared with the server.
//
// Zuzu's AI check guesses one when a thought is posted, and the writer can change
// it with one tap. Used to filter the Sky, for "others felt this too", and later
// for Emotional Weather. Not the same as FEELINGS (how someone felt after a chat).
// The emoji on a thought stays the writer's own; the feeling is a separate tag.

export const SKY_FEELINGS = [
  { key: 'lonely', label: 'Lonely', words: ['lonely', 'alone', 'unseen', 'nobody', 'no one', 'isolated', 'invisible', 'left out'] },
  { key: 'heartbreak', label: 'Heartbreak', words: ['breakup', 'broke up', 'ex ', 'miss him', 'miss her', 'relationship', 'love', 'heart', 'cheated', 'ghosted'] },
  { key: 'work', label: 'Stress and work', words: ['work', 'job', 'placement', 'interview', 'business', 'career', 'boss', 'office', 'hiring', 'resume', ' cv ', 'salary', 'promotion', 'appraisal', 'layoff', 'laid off', 'fired', 'unemployed', 'internship', 'startup', 'client', 'manager', 'colleague', 'coworker', 'exam', 'study', 'college', 'deadline', 'stress', 'burnout', 'tired'] },
  { key: 'family', label: 'Family', words: ['family', 'parents', 'mother', 'father', 'mom', 'dad', 'brother', 'sister', 'home'] },
  { key: 'selfworth', label: 'Self-worth', words: ['not enough', 'worthless', 'failure', 'hate myself', 'ugly', 'useless', 'proud', 'validation', 'confidence'] },
  { key: 'anxiety', label: 'Anxiety', words: ['anxious', 'anxiety', 'panic', 'worried', 'worry', 'scared', 'fear', 'overthinking', "can't sleep", 'night'] },
  { key: 'grief', label: 'Grief', words: ['died', 'death', 'passed away', 'grief', 'funeral', 'lost my', 'gone forever'] },
  { key: 'anger', label: 'Anger', words: ['angry', 'anger', 'furious', 'hate', 'unfair', 'mad at', 'annoyed', 'frustrated'] },
  { key: 'hope', label: 'Hope', words: ['hope', 'grateful', 'thankful', 'better', 'happy', 'healing', 'proud of', 'excited'] },
  { key: 'other', label: 'Other emotions', words: [] },
] as const;

export type SkyFeelingKey = typeof SKY_FEELINGS[number]['key'];

export const isSkyFeeling = (k: unknown): k is SkyFeelingKey => SKY_FEELINGS.some(f => f.key === k);

export const skyFeelingLabel = (k?: string) => SKY_FEELINGS.find(f => f.key === k)?.label ?? 'Other emotions';

// How the feeling reads in "N others felt ___ this month".
const FELT: Record<string, string> = {
  lonely: 'lonely', heartbreak: 'heartbreak', work: 'stressed about work', family: 'this about family',
  selfworth: 'this about themselves', anxiety: 'anxious', grief: 'grief', anger: 'angry', hope: 'hopeful',
  other: 'something like this',
};
export const feltPhrase = (k?: string) => FELT[k ?? 'other'] ?? 'something like this';

// A thought can be about more than one thing: up to this many feelings, main one first.
export const MAX_FEELINGS = 3;

// Keep only known feelings, no repeats, at most MAX_FEELINGS.
export function cleanFeelings(list: unknown): SkyFeelingKey[] {
  const arr = Array.isArray(list) ? list : typeof list === 'string' ? [list] : [];
  return [...new Set(arr.filter(isSkyFeeling))].slice(0, MAX_FEELINGS);
}

// A simple word match, used only when the AI can't answer: the one or two best.
export function guessFeelingFromWords(text: string): SkyFeelingKey[] {
  const t = ` ${text.toLowerCase()} `;
  const hits = SKY_FEELINGS
    .map(f => ({ key: f.key, n: f.words.filter(w => t.includes(w)).length }))
    .filter(h => h.n > 0)
    .sort((a, b) => b.n - a.n)
    .slice(0, 2)
    .map(h => h.key);
  return hits.length ? hits : ['other'];
}

// Clearly about work or studies, as whole words (used to fix older thoughts
// whose feelings missed "work"; new ones get it from the AI).
export const WORK_WORDS = /\b(job|jobs|job hunt|placements?|interviews?|business|career|internships?|hiring|resume|salary|promotion|appraisal|layoffs?|laid off|unemployed|jobless|boss|office|workplace|colleagues?|coworkers?|deadlines?|exams?|startup)\b/i;

// Below this, counts are shown softly ("a few others") instead of as a number.
export const FEELING_COUNT_MIN = 10;
